import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages the desktop header menu.
 * The menu follows the reference theme pattern: one active top-level item at a time,
 * with the submenu wrapper and its content revealing together.
 */
class HeaderMenu extends Component {
  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;
  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimer = null;
  /**
   * @type {{ activeItem: HTMLElement | null }}
   */
  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#outsideClickListener);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#outsideClickListener);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#cancelDeactivate();
    this.#cleanupResizeObserver();
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Open the hovered or focused menu item.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const submenu = findSubmenu(item);
    if (!submenu) {
      this.#deactivate();
      return;
    }

    if (item === this.#state.activeItem) {
      this.#cancelDeactivate();
      this.#updateSubmenuHeight(submenu);
      return;
    }

    this.#openItem(item, submenu);
  };

  /**
   * Toggle the clicked top-level menu item.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const submenu = findSubmenu(item);
    if (!submenu) {
      this.#deactivate();
      return;
    }

    event.preventDefault();

    if (this.#state.activeItem === item) {
      this.#deactivate(item);
      return;
    }

    this.#openItem(item, submenu);
  };

  /**
   * Close the active menu if the pointer or focus leaves the menu region.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate = (event) => {
    if (!(event.target instanceof Element)) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const activeSubmenu = findSubmenu(activeItem);
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;

    if (relatedTarget && (activeItem.contains(relatedTarget) || activeSubmenu?.contains(relatedTarget))) {
      return;
    }

    const isLeavingSubmenu = Boolean(event.target.closest('[ref="submenu[]"]'));
    const delay = event.type === 'pointerleave' && !isLeavingSubmenu ? 140 : 0;
    this.#scheduleDeactivate(delay);
  };

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();

    if (this.headerComponent?.dataset.menuStyle === 'drawer') {
      this.#deactivate();
      return;
    }

    const activeSubmenu = findSubmenu(this.#state.activeItem);
    if (activeSubmenu) {
      this.#updateSubmenuHeight(activeSubmenu);
    }
  }, 100);

  #outsideClickListener = (event) => {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof Node)) return;

    if (!this.contains(target)) {
      this.#deactivate();
    }
  };

  /**
   * Close the active menu with Escape.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    this.#deactivate();
    activeItem.focus();
  };

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   */
  #openItem(item, submenu) {
    this.#cancelDeactivate();

    if (this.#state.activeItem && this.#state.activeItem !== item) {
      this.#closeItem(this.#state.activeItem);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    submenu.dataset.active = '';

    this.#observeSubmenu(submenu);
    this.#updateSubmenuHeight(submenu);
  }

  /**
   * @param {HTMLElement} item
   */
  #closeItem(item) {
    const submenu = findSubmenu(item);

    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
      submenu.style.removeProperty('--submenu-panel-height');
    }

    this.#cleanupResizeObserver();
  }

  /**
   * @param {number} delay
   */
  #scheduleDeactivate(delay) {
    this.#cancelDeactivate();

    if (delay === 0) {
      this.#deactivate();
      return;
    }

    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = null;
      this.#deactivate();
    }, delay);
  }

  #cancelDeactivate() {
    if (this.#deactivateTimer) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = null;
    }
  }

  /**
   * Deactivate the active item immediately.
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.#cancelDeactivate();
    this.#closeItem(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
  };

  /**
   * @param {HTMLElement} submenu
   */
  #observeSubmenu(submenu) {
    this.#cleanupResizeObserver();

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (!(inner instanceof HTMLElement)) return;

    this.#submenuResizeObserver = new ResizeObserver(() => {
      if (this.#state.activeItem && findSubmenu(this.#state.activeItem) === submenu) {
        this.#updateSubmenuHeight(submenu);
      }
    });

    this.#submenuResizeObserver.observe(inner);
  }

  /**
   * @param {HTMLElement} submenu
   */
  #updateSubmenuHeight(submenu) {
    if (!this.headerComponent) return;
    const activeItem = this.#state.activeItem;
    if (!activeItem || findSubmenu(activeItem) !== submenu) return;

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    if (!(inner instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      const currentActiveItem = this.#state.activeItem;
      if (!currentActiveItem || findSubmenu(currentActiveItem) !== submenu) return;

      const height = inner.scrollHeight;
      submenu.style.setProperty('--submenu-panel-height', `${height}px`);
      this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
      this.#setFullOpenHeaderHeight(height);
    });
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const visibleTopRow = findVisibleTopRow(this.headerComponent);

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? visibleTopRow?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item trigger.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const closestMenuItem = element.closest('[ref="menuitem"]');
  if (closestMenuItem instanceof HTMLElement) {
    return closestMenuItem;
  }

  const queryResult = element.querySelector('[ref="menuitem"]');
  return queryResult instanceof HTMLElement ? queryResult : null;
}

/**
 * Find the submenu associated with a menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Find the first header top row that is currently rendered.
 * @param {ParentNode | null} root
 * @returns {HTMLElement | null}
 */
function findVisibleTopRow(root) {
  const rows = root?.querySelectorAll?.('.header__row--top');
  if (!rows) return null;

  for (const row of rows) {
    if (row instanceof HTMLElement && row.getClientRects().length > 0) {
      return row;
    }
  }

  return null;
}
